import { Component, OnInit } from '@angular/core';

import { Router } from '@angular/router';
import { MatTableDataSource } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { FormsModule } from '@angular/forms';
import { PageEvent } from '@angular/material/paginator';
import { IllustrationsTableComponent } from '../illustrations-table/illustrations-table.component';
import { IllustrationData, CustomerInfo, FeeData } from '../shared/illustrations.interface';
import { SharedFiltersComponent } from '../../../shared/components/shared-filters/shared-filters.component';
import { ContractFilter, IllustrationItem } from '../../../interfaces';
import { IllustrationsService } from '../../../services/illustrations.service';
import { AuthService } from '../../../services/auth.service';

@Component({
  selector: 'app-illustrations-wrapper',
  standalone: true,
  imports: [
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    MatNativeDateModule,
    IllustrationsTableComponent,
    SharedFiltersComponent
],
  templateUrl: './illustrations-wrapper.component.html',
  styleUrls: ['./illustrations-wrapper.component.scss']
})
export class IllustrationsWrapperComponent implements OnInit {

  constructor(
    private router: Router,
    private illustrationsService: IllustrationsService,
    private authService: AuthService
  ) {}

  dataSource = new MatTableDataSource<IllustrationData>();
  
  // Store original data for contact filtering
  originalIllustrationData: IllustrationItem[] = [];
  
  // Store all converted illustration data for pagination
  allIllustrationData: IllustrationData[] = [];
  
  // Egyedi pagination properties az illusztrációk száma alapján
  totalIllustrations: number = 0;
  currentPage: number = 0;
  pageSize: number = 10;
  
  // Shared filters
  currentSharedFilter: ContractFilter = {
    searchTerm: '',
    statusFilter: [],
    biztositoFilter: [],
    assigneeFilter: [],
    dateFromFilter: null,
    dateToFilter: null,
    productFilter: [],
    paymentFrequencyFilter: [],
    currencyFilter: [],
    policyNumberFilter: '',
    illustrationStatusFilter: []
  };


  
  // Options for shared filters - dynamically generated from actual data
  customKumOptions: { value: string; label: string }[] = [];
  customAreaOptions: { value: string; label: string }[] = [];
  illustrationStatusOptions: { value: string; label: string }[] = [];
  
  // Lock KÜM filter for KÜM role users
  lockKumFilter: boolean = false;

  ngOnInit() {
    // Check if current user is KÜM - if so, lock KÜM filter
    const currentUserRole = this.authService.getCurrentUserRole();
    if (currentUserRole === 'kam') {
      this.lockKumFilter = true;
      console.log('🔒 KÜM felhasználó: KÜM szűrő letiltva');
    }
    
    this.loadData();
  }

  loadData() {
    this.illustrationsService.getIllustrations().subscribe({
      next: (response) => {
        if (response.success && response.data) {
          // Store original data for contact filtering
          this.originalIllustrationData = response.data;
          
          // Group backend data by illustrationid and combine categories into single rows
          const groupedData = this.groupIllustrationsByCategory(response.data);
          const illustrationData = groupedData.map(item => this.convertToIllustrationData(item));
          
          // Store all data for pagination
          this.allIllustrationData = illustrationData;
          this.dataSource.data = illustrationData;
          
          // Beállítjuk az kezdeti pagination értékeket
          const uniqueIds = [...new Set(illustrationData.map(item => item.originalIllustrationId || item.id))];
          this.totalIllustrations = uniqueIds.length;
          this.currentPage = 0;
          
          // Update filter options based on actual data
          this.updateFilterOptions(illustrationData);
          
          // Kezdeti pagination alkalmazása
          this.applyPaginationToFilteredData(illustrationData);
          
          // Ha KÜM felhasználó, automatikusan beállítjuk a saját nevét a szűrőbe
          if (this.lockKumFilter) {
            const currentUser = this.authService.getCurrentUser();
            if (currentUser && (currentUser.username || currentUser.name)) {
              const currentUserName = currentUser.username || currentUser.name;
              this.currentSharedFilter.statusFilter = [currentUserName];
              console.log('🔒 KÜM automatikus szűrés beállítva:', currentUserName);
            }
          }
          
          // Initialize filtering
          this.applySharedFilters();
          
          console.log('✅ Illusztrációk betöltve:', illustrationData);
        }
      },
      error: (error) => {
        console.error('❌ Hiba az illusztrációk betöltésekor:', error);
      }
    });
  }

  /**
   * Group illustrations by illustrationid and combine categories into grouped data
   */
  private groupIllustrationsByCategory(items: IllustrationItem[]): any[] {
    const result: any[] = [];
    const processedIllustrations = new Map<number, number>();
    
    items.forEach(item => {
      const illustrationId = item.illustrationid;
      
      // Count how many areas this illustration has
      const areasCount = items.filter(i => i.illustrationid === illustrationId).length;
      const isFirstAreaForIllustration = !processedIllustrations.has(illustrationId);
      
      if (isFirstAreaForIllustration) {
        processedIllustrations.set(illustrationId, areasCount);
      }
      
      // Parse fees and other_entities for this area
      let fees = [];
      let otherEntities = [];
      
      if (typeof (item as any).fees === 'string') {
        try {
          fees = JSON.parse((item as any).fees) || [];
        } catch (e) {
          console.warn('Failed to parse fees:', (item as any).fees);
        }
      } else if (Array.isArray((item as any).fees)) {
        fees = (item as any).fees;
      }
      
      if (typeof (item as any).other_entities === 'string') {
        try {
          otherEntities = JSON.parse((item as any).other_entities) || [];
        } catch (e) {
          console.warn('Failed to parse other_entities:', (item as any).other_entities);
        }
      } else if (Array.isArray((item as any).other_entities)) {
        otherEntities = (item as any).other_entities;
      }
      
      // Create a row for this specific area
      result.push({
        ...item,
        // Add area-specific data
        currentArea: {
          category_id: (item as any).category_id,
          category: (item as any).category,
          notes: (item as any).notes,
          fees: fees,
          other_entities: otherEntities,
          customers: item.customers || []
        },
        // Add rowspan information for colspan cells
        isFirstRowForIllustration: isFirstAreaForIllustration,
        areasCount: areasCount,
        // Store original illustration data
        originalIllustrationId: illustrationId
      });
    });
    
    return result;
  }

  /**
   * Convert area-specific data to frontend IllustrationData format
   * Creates one row per area within each illustration
   */
  private convertToIllustrationData(item: any): IllustrationData {
    // Get email and phone from company contacts (prefer company-level contacts)
    const companyEmail = item.company_contacts?.find((c: any) => c.contact_type === 'email')?.contact_value || '';
    const companyPhone = item.company_contacts?.find((c: any) => c.contact_type === 'phone')?.contact_value || '';

    // Collect all customer emails and phones as fallback
    const allCustomerEmails = item.customer_contacts?.filter((c: any) => c.contact_type === 'email').map((c: any) => c.contact_value) || [];
    const allCustomerPhones = item.customer_contacts?.filter((c: any) => c.contact_type === 'phone').map((c: any) => c.contact_value) || [];
    
    // Use company contacts if available, otherwise first customer contact
    const primaryEmail = companyEmail || allCustomerEmails[0] || '';
    const primaryPhone = companyPhone || allCustomerPhones[0] || '';

    // Calculate persons count for current area only
    const areaCustomersCount = item.currentArea?.customers?.length || 0;
    const areaOtherEntitiesCount = item.currentArea?.other_entities?.length || 0;

    return {
      id: item.illustrationid,
      contactDate: new Date(item.createdat), // Kapcsolatfelvétel ideje
      sendDate: new Date(item.senddate), // Kiküldés napja
      kum: item.user_nickname || item.user_fullname || '', // KÜM
      companyName: item.companyname || '', // Cég neve
      taxNumber: item.taxnumber || '', // Adószám
      // Area-specific customer data
      customers: item.currentArea?.customers || [], // Customers for this specific area
      otherEntities: item.currentArea?.other_entities || [], // Other entities for this specific area
      customerName: '', // Will be handled in template
      birthDate: new Date(),
      age: 0,
      ownership: '', // Maradjon üres a kérés szerint
      area: item.currentArea?.category || '', // Current area only
      areas: [item.currentArea], // Single area for this row
      mainPersons: areaCustomersCount + areaOtherEntitiesCount, // Persons count for this area only
      fees: item.currentArea?.fees || [], // Fees for this area only
      notes: item.currentArea?.notes || '', // Notes for this area only
      email: primaryEmail, // Elérhetőség
      phone: primaryPhone, // Elérhetőség
      status: item.status || '', // Status of the illustration
      // Colspan information
      isFirstRowForIllustration: item.isFirstRowForIllustration,
      areasCount: item.areasCount,
      originalIllustrationId: item.originalIllustrationId
    } as any;
  }

  /**
   * Calculate age from birthdate
   */
  private calculateAge(birthDate: Date): number {
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    return age;
  }

  /**
   * Update filter options based on loaded data
   */
  private updateFilterOptions(data: IllustrationData[]) {
    // Update KÜM options
    const kumSet = new Set(data.map(d => d.kum).filter(k => k));
    
    // Update area options
    const areaSet = new Set(data.map(d => d.area).filter(a => a));
    
    // Update custom KÜM options for shared filters (státusz helyett)
    const uniqueKums = Array.from(kumSet).sort();
    this.customKumOptions = uniqueKums.map(kum => ({
      value: kum,
      label: kum
    }));
    
    // Update custom Area options for shared filters (biztosító helyett)
    const uniqueAreas = Array.from(areaSet).sort();
    this.customAreaOptions = uniqueAreas.map(area => ({
      value: area,
      label: area
    }));
    
    // Update illustration status options
    const statusSet = new Set(data.map(d => d.status).filter(s => s));
    const uniqueStatuses = Array.from(statusSet).sort();
    this.illustrationStatusOptions = uniqueStatuses.map(status => ({
      value: status,
      label: status
    }));
  }



  isDateInRange(date: Date, fromDate: Date | null, toDate: Date | null): boolean {
    // This method is no longer used, but keeping for compatibility
    return true;
  }



  createNewIllustration() {
    this.router.navigate(['/add-new-illustration']);
  }

  onSharedFilterChange(filter: ContractFilter) {
    this.currentSharedFilter = filter;
    this.applySharedFilters();
  }

  onSharedFilterClear() {
    this.currentSharedFilter = {
      searchTerm: '',
      statusFilter: [],
      biztositoFilter: [],
      assigneeFilter: [],
      dateFromFilter: null,
      dateToFilter: null,
      productFilter: [],
      paymentFrequencyFilter: [],
      currencyFilter: [],
      policyNumberFilter: '',
      illustrationStatusFilter: []
    };
    this.applySharedFilters();
  }

  applySharedFilters() {
    // Egyszerűbb megközelítés: külön kezeljük a szűrést és a paginációt
    // Először az összes adatból szűrünk
    let filteredData = this.allIllustrationData;

    // Helper function to check if search term matches any customer names or addresses
    const checkCustomersMatch = (data: IllustrationData, searchTerm: string): boolean => {
      if (!data.customers || data.customers.length === 0) {
        return false;
      }
      
      return data.customers.some(customer => 
        customer.fullname.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (customer.address && customer.address.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    };

    // Helper function to check all contact information (emails and phones) from raw data
    const checkAllContactsMatch = (data: IllustrationData, searchTerm: string): boolean => {
      // Get the original item to access all contact data
      const originalItem = this.originalIllustrationData.find((item: IllustrationItem) => item.illustrationid === data.id);
      if (!originalItem) return false;

      // Check company contacts
      if (originalItem.company_contacts) {
        const companyContactsMatch = originalItem.company_contacts.some((contact: any) =>
          contact.contact_value.toLowerCase().includes(searchTerm.toLowerCase())
        );
        if (companyContactsMatch) return true;
      }

      // Check customer contacts
      if (originalItem.customer_contacts) {
        const customerContactsMatch = originalItem.customer_contacts.some((contact: any) =>
          contact.contact_value.toLowerCase().includes(searchTerm.toLowerCase())
        );
        if (customerContactsMatch) return true;
      }

      return false;
    };

    // Alkalmazzuk a szűrőket
    if (this.currentSharedFilter.searchTerm) {
      filteredData = filteredData.filter(data => 
        data.companyName.toLowerCase().includes(this.currentSharedFilter.searchTerm.toLowerCase()) ||
        data.taxNumber.includes(this.currentSharedFilter.searchTerm) ||
        data.customerName.toLowerCase().includes(this.currentSharedFilter.searchTerm.toLowerCase()) ||
        checkCustomersMatch(data, this.currentSharedFilter.searchTerm) ||
        checkAllContactsMatch(data, this.currentSharedFilter.searchTerm) ||
        data.kum.toLowerCase().includes(this.currentSharedFilter.searchTerm.toLowerCase()) ||
        data.area.toLowerCase().includes(this.currentSharedFilter.searchTerm.toLowerCase()) ||
        data.notes.toLowerCase().includes(this.currentSharedFilter.searchTerm.toLowerCase()) ||
        data.email.toLowerCase().includes(this.currentSharedFilter.searchTerm.toLowerCase()) ||
        data.phone.includes(this.currentSharedFilter.searchTerm)
      );
    }

    // KÜM szűrő
    if (this.currentSharedFilter.statusFilter.length > 0) {
      filteredData = filteredData.filter(data => 
        this.currentSharedFilter.statusFilter.includes(data.kum)
      );
    }

    // Terület szűrő - egész illusztráció megjelenítése, ha legalább egy területe megfelel
    if (this.currentSharedFilter.biztositoFilter.length > 0) {
      // Először megkeressük azokat az illusztráció ID-kat, amelyeknek legalább egy területe megfelel a szűrőnek
      const matchingIllustrationIds = new Set<number>();
      
      // Végigmegyünk az összes adaton és összegyűjtjük a megfelelő illusztráció ID-kat
      this.allIllustrationData.forEach(data => {
        if (this.currentSharedFilter.biztositoFilter.includes(data.area)) {
          matchingIllustrationIds.add(data.originalIllustrationId || data.id);
        }
      });
      
      // Csak azokat az adatokat tartjuk meg, amelyek illusztráció ID-ja szerepel a megfelelők listájában
      filteredData = filteredData.filter(data => 
        matchingIllustrationIds.has(data.originalIllustrationId || data.id)
      );
    }

    // Illustration Status szűrő
    if (this.currentSharedFilter.illustrationStatusFilter && this.currentSharedFilter.illustrationStatusFilter.length > 0) {
      filteredData = filteredData.filter(data => 
        this.currentSharedFilter.illustrationStatusFilter!.includes(data.status)
      );
    }

    // Dátum szűrők
    if (this.currentSharedFilter.dateFromFilter) {
      filteredData = filteredData.filter(data => {
        const contactDate = new Date(data.contactDate);
        const filterDate = new Date(this.currentSharedFilter.dateFromFilter!);
        
        const contactDateStr = contactDate.getFullYear() + '-' + 
          String(contactDate.getMonth() + 1).padStart(2, '0') + '-' + 
          String(contactDate.getDate()).padStart(2, '0');
        const filterDateStr = filterDate.getFullYear() + '-' + 
          String(filterDate.getMonth() + 1).padStart(2, '0') + '-' + 
          String(filterDate.getDate()).padStart(2, '0');
        
        return contactDateStr === filterDateStr;
      });
    }

    if (this.currentSharedFilter.dateToFilter) {
      filteredData = filteredData.filter(data => {
        const sendDate = new Date(data.sendDate);
        const filterDate = new Date(this.currentSharedFilter.dateToFilter!);
        
        const sendDateStr = sendDate.getFullYear() + '-' + 
          String(sendDate.getMonth() + 1).padStart(2, '0') + '-' + 
          String(sendDate.getDate()).padStart(2, '0');
        const filterDateStr = filterDate.getFullYear() + '-' + 
          String(filterDate.getMonth() + 1).padStart(2, '0') + '-' + 
          String(filterDate.getDate()).padStart(2, '0');
        
        return sendDateStr === filterDateStr;
      });
    }

    // Pagination info frissítése a szűrt adatok alapján
    const uniqueIds = [...new Set(filteredData.map(item => item.originalIllustrationId || item.id))];
    this.totalIllustrations = uniqueIds.length;
    
    // Ha az aktuális oldal túl nagy, visszaállítjuk az elsőre
    const maxPage = Math.max(0, Math.ceil(this.totalIllustrations / this.pageSize) - 1);
    if (this.currentPage > maxPage) {
      this.currentPage = 0;
    }

    // Alkalmazunk pagination-t a szűrt adatokra
    this.applyPaginationToFilteredData(filteredData);
  }

  /**
   * Pagination alkalmazása a szűrt adatokra
   */
  applyPaginationToFilteredData(filteredData: IllustrationData[]) {
    // Egyedi illusztrációk ID-i
    const uniqueIds = [...new Set(filteredData.map(item => item.originalIllustrationId || item.id))];

    // Aktuális oldalra tartozó illusztrációk
    const startIndex = this.currentPage * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    const illustrationsToShow = uniqueIds.slice(startIndex, endIndex);

    // Szűrjük az adatokat, hogy csak a megfelelő illusztrációk sorai jelenjenek meg
    const dataToShow = filteredData.filter(item => 
      illustrationsToShow.includes(item.originalIllustrationId || item.id)
    );

    // Frissítjük a dataSource-t
    this.dataSource.data = dataToShow;
  }

  /**
   * Pagination esemény kezelése
   */
  onPageChange(event: PageEvent) {
    this.currentPage = event.pageIndex;
    this.pageSize = event.pageSize;
    
    // Újra alkalmazzuk a szűréseket és a paginációt
    this.applySharedFilters();
  }

  /**
   * Status update event handler
   */
  onStatusUpdated(): void {
    console.log('📄 Status updated, reloading data...');
    // Reload the data to reflect the status change
    this.loadData();
  }
}
